import { useState, type KeyboardEvent } from "react";
import { MicButton, type MicState } from "./MicButton";

/**
 * 前半の画面 (spec 2026-08-04 D2)。**画面はマイクと状態の一言だけ。**
 *
 * 設問文も、聞き取った回答も、お礼も出さない — 画面に設問が読める限り、被験者は
 * 聞かずに読んで答えられてしまい「音声だけで成立するか」を測れない (spec §1.2)。
 * 一言 (「どうぞ」「聞いています」…) は会話の中身を含まないので、この検証は
 * 成立したまま「今どっちの番か」だけを伝えられる (§5.1)。
 *
 * 音声が通らなくなったときだけ、テキスト入力が自動で現れる (D10)。平常時に
 * 何も出さないぶん、詰まったときの逃げ道は必須 (CLAUDE.md 原則 5)。
 * **畳んだリンクとしても出さない** — 開く操作のために画面を見ることになるため、
 * 条件を満たしたら開いた状態でいきなり出す。
 */
type Props = {
  micState: MicState;
  onStartRecord: () => void;
  onSubmitText: (text: string) => void;
  /** マイクが使えない (?preflight=skip でマイク拒否)。テキスト入力を開く */
  micDisabled?: boolean;
  /** 構造化の待ちなど、今は受け付けられない */
  inputDisabled?: boolean;
  /** STT の連続失敗・無音 level 1。テキスト入力を開く */
  forceTextFallback?: boolean;
  /**
   * 会話ログ (`?log=1`) と並べるとき。画面を占有せず上部に小さく置く。
   * 実際のユーザーが見る画面との差が「ログの有無」だけになるようにするため、
   * ログを出すときもマイクは消さない (spec §3.4)。
   */
  compact?: boolean;
};

export function VoiceStage({
  micState,
  onStartRecord,
  onSubmitText,
  micDisabled,
  inputDisabled,
  forceTextFallback,
  compact,
}: Props) {
  const [text, setText] = useState("");
  const showText = Boolean(micDisabled || forceTextFallback);

  const submit = () => {
    if (inputDisabled) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    onSubmitText(trimmed);
    setText("");
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div
      className={
        compact
          ? "shrink-0 flex flex-col items-center gap-3 px-6 py-4 border-b border-slate-200 bg-white"
          : "flex-1 min-h-0 flex flex-col items-center justify-center gap-6 px-6 py-8"
      }
    >
      <MicButton
        state={micState}
        onStart={onStartRecord}
        size={compact ? "sm" : "lg"}
        disabled={micDisabled}
        ariaLabel="マイク"
      />
      {showText && (
        <div className="w-full max-w-sm flex flex-col items-center gap-2">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKey}
            disabled={inputDisabled}
            placeholder="文字で入力 (Enter で送信)"
            // text-base (16px) は iOS Safari が focus 時に画面を強制ズームしない下限。
            // 音声で詰まってここへ落ちた人が踏む唯一の入口なので 14px には戻さない。
            className="w-full bg-slate-100 rounded-full px-4 py-2 text-base text-slate-700 min-h-[44px] disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-indigo-300"
          />
        </div>
      )}
    </div>
  );
}
